import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import { API, useAuth } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ChevronLeft, ChevronRight, Send, Loader2, UserRound, Search, ShieldAlert,
} from 'lucide-react';
import { COUNSELOR_FOLLOWUP_SCHEMA } from '@/schemas/intake_schemas';
import {
  SchemaSection, SectionNav, validateSchema, overallCompleteness,
} from '@/components/forms/SchemaFormRenderer';
import AdminPerceptionStatusPill from '@/components/AdminPerceptionStatusPill';
import PerceptionContextSnippet from '@/components/PerceptionContextSnippet';
import PatternHintsBanner from '@/components/PatternHintsBanner';

function IdentityStrip({ sub, intakeSummary, viewerRole }) {
  // IMPORTANT: Counselors MUST NOT see canonical context or system suggestions.
  // This strip shows identity + a minimal intake summary (status + submit date +
  // a few basics) — nothing from canonical_context or system_suggestions.
  const basics = intakeSummary?.basics || {};
  return (
    <Card className="bg-[#FBFAF7] border-[#EBE8E3]" data-testid="counselor-identity-strip">
      <CardContent className="p-4">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="h-10 w-10 rounded-full bg-[#2C2A29] text-white flex items-center justify-center flex-shrink-0">
            <UserRound className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="text-sm font-semibold text-[#1C1A19]">{sub?.name || sub?.email || 'Subscriber'}</div>
            <div className="text-xs text-[#5C5955]">{sub?.email}</div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {basics.age ? <Badge variant="outline" className="text-[10px]">Age {basics.age}</Badge> : null}
              {basics.gender ? <Badge variant="outline" className="text-[10px]">{basics.gender}</Badge> : null}
              {basics.city ? <Badge variant="outline" className="text-[10px]">{basics.city}</Badge> : null}
              {(sub?.conditions || []).map(c => (
                <Badge key={c} variant="secondary" className="text-[10px] bg-[#F5EEDC] text-[#7A5A2B]">{c}</Badge>
              ))}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] text-[#8A8681]">Intake status</div>
            <Badge
              variant="secondary"
              className={intakeSummary?.status === 'submitted' ? 'bg-[#E7EFE9] text-[#2F5A40]' : 'bg-[#F5EEDC] text-[#7A5A2B]'}
              data-testid="counselor-intake-status"
            >
              {intakeSummary?.status || 'no intake'}
            </Badge>
            {intakeSummary?.submitted_at ? (
              <div className="text-[10px] text-[#8A8681] mt-0.5">
                Submitted {new Date(intakeSummary.submitted_at).toLocaleDateString()}
              </div>
            ) : null}
          </div>
        </div>

        <div className="mt-3 rounded-md border border-[#E3DFD7] bg-white p-2.5 text-[11px] text-[#5C5955] flex items-start gap-2">
          <ShieldAlert className="h-3.5 w-3.5 text-[#C89B7B] mt-0.5 flex-shrink-0" />
          <span>
            You can submit follow-up observations for this subscriber. Canonical Biological
            Context and system suggestions are reserved for Admin review.
          </span>
        </div>
        <div className="mt-2">
          <AdminPerceptionStatusPill subscriberId={sub?.id} compact />
        </div>
        {sub?.id && (
          <div className="mt-3" data-testid="counselor-pattern-hints">
            <p className="text-[10px] uppercase tracking-wider text-[#8A857C] mb-1">Pattern Hints</p>
            <PatternHintsBanner subscriberId={sub.id} viewerRole={viewerRole} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SubscriberPicker({ subscribers, query, onQuery, onSelect }) {
  const filtered = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return subscribers.slice(0, 30);
    return subscribers.filter(s =>
      (s.name || '').toLowerCase().includes(q) ||
      (s.email || '').toLowerCase().includes(q)
    ).slice(0, 30);
  }, [subscribers, query]);

  return (
    <Card className="bg-white border-[#EBE8E3]" data-testid="counselor-sub-picker">
      <CardContent className="p-5">
        <h2 className="text-lg font-semibold text-[#1C1A19] mb-1">Select a subscriber</h2>
        <p className="text-xs text-[#5C5955] mb-4">Choose the subscriber you want to submit a follow-up for.</p>
        <div className="relative mb-3">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8681]" />
          <Input
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Search by name or email"
            className="pl-9 bg-white border-[#EBE8E3]"
            data-testid="counselor-sub-search"
          />
        </div>
        <div className="max-h-[520px] overflow-auto divide-y divide-[#EBE8E3]">
          {filtered.length === 0 ? (
            <p className="text-sm text-[#8A8681] py-4 text-center">No subscribers match.</p>
          ) : filtered.map(s => (
            <button
              key={s.id}
              type="button"
              onClick={() => onSelect(s.id)}
              data-testid={`counselor-sub-pick-${s.id}`}
              className="w-full text-left py-2 px-1 flex items-center gap-3 hover:bg-[#FAF8F4] rounded-md transition-colors"
            >
              <div className="h-8 w-8 rounded-full bg-[#2C2A29] text-white flex items-center justify-center text-xs font-semibold">
                {(s.name || s.email || '?').slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#1C1A19] truncate">{s.name || '—'}</div>
                <div className="text-[11px] text-[#8A8681] truncate">{s.email}</div>
              </div>
              <ChevronRight className="h-4 w-4 text-[#8A8681]" />
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function CounselorFollowUp() {
  const { subId: subIdParam } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [subscribers, setSubscribers] = useState([]);
  const [query, setQuery] = useState('');
  const [selectedSubId, setSelectedSubId] = useState(subIdParam || '');
  const [selectedSub, setSelectedSub] = useState(null);
  const [intakeSummary, setIntakeSummary] = useState(null);

  const [answers, setAnswers] = useState({});
  const [activeKey, setActiveKey] = useState(COUNSELOR_FOLLOWUP_SCHEMA[0].key);
  const [submitting, setSubmitting] = useState(false);

  // ── Load subscribers list (admin + counselor can read /api/users?role=subscriber)
  useEffect(() => {
    axios.get(`${API}/users?role=subscriber`)
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.users || []);
        setSubscribers(list);
      })
      .catch(e => {
        console.error(e);
        toast.error('Could not load subscribers');
      });
  }, []);

  // ── Load selected subscriber detail + intake summary (no canonical context)
  useEffect(() => {
    if (!selectedSubId) { setSelectedSub(null); setIntakeSummary(null); return; }
    (async () => {
      const sub = subscribers.find(s => s.id === selectedSubId);
      setSelectedSub(sub || { id: selectedSubId });

      // Pre-fill identity on follow-up (name etc) for counselor convenience
      if (sub) {
        const nameParts = (sub.name || '').split(' ');
        setAnswers(prev => ({
          ...prev,
          record_linking: {
            ...(prev.record_linking || {}),
            first_name: (prev.record_linking?.first_name) || nameParts[0] || '',
            last_name: (prev.record_linking?.last_name) || nameParts.slice(1).join(' ') || '',
            linked_subscriber_id: selectedSubId,
            counselor_name: (prev.record_linking?.counselor_name) || (user?.name || user?.email || ''),
            followup_date: (prev.record_linking?.followup_date) || new Date().toISOString().slice(0, 10),
          },
        }));
      }

      // Try to fetch subscriber intake summary (counselor allowed)
      try {
        const r = await axios.get(`${API}/master-intake/${selectedSubId}/summary`);
        setIntakeSummary({
          status: r.data?.status || null,
          submitted_at: r.data?.submitted_at || null,
          basics: r.data?.basics || {},
        });
      } catch (e) {
        setIntakeSummary({ status: null, submitted_at: null, basics: {} });
      }
    })();
  }, [selectedSubId, subscribers]); // eslint-disable-line

  const activeSection = useMemo(
    () => COUNSELOR_FOLLOWUP_SCHEMA.find(s => s.key === activeKey) || COUNSELOR_FOLLOWUP_SCHEMA[0],
    [activeKey]
  );
  const activeIndex = COUNSELOR_FOLLOWUP_SCHEMA.findIndex(s => s.key === activeKey);
  const isLast = activeIndex === COUNSELOR_FOLLOWUP_SCHEMA.length - 1;
  const isFirst = activeIndex === 0;
  const completeness = overallCompleteness(COUNSELOR_FOLLOWUP_SCHEMA, answers);
  const errors = validateSchema(COUNSELOR_FOLLOWUP_SCHEMA, answers);

  const onSectionChange = (sectionKey, sectionAnswers) => {
    setAnswers(prev => ({ ...prev, [sectionKey]: sectionAnswers }));
  };

  const submit = useCallback(async () => {
    if (!selectedSubId) { toast.error('Please select a subscriber first.'); return; }
    if (errors.length > 0) {
      toast.error(`Please complete: ${errors.slice(0, 2).map(e => e.label).join(', ')}${errors.length > 2 ? '…' : ''}`);
      setActiveKey(errors[0].section);
      return;
    }
    try {
      setSubmitting(true);
      // Short textual summary pieced together from Counselor Summary block if available
      const sum = answers.counselor_summary || {};
      const content = [
        sum.top_triggers && `Triggers: ${sum.top_triggers}`,
        sum.top_relief && `Relief: ${sum.top_relief}`,
        sum.symptom_sequence && `Sequence: ${sum.symptom_sequence}`,
        sum.terrain_reading && `Terrain: ${sum.terrain_reading}`,
      ].filter(Boolean).join(' | ') || 'Counselor follow-up submitted.';

      await axios.post(`${API}/biocontext-memory/counselor/submit/${selectedSubId}`, {
        kind: 'followup',
        content,
        structured_notes: answers,  // full schema-driven payload routed to input_queue
        tags: ['counselor_followup_form'],
      });
      toast.success('Follow-up submitted');
      // Clear form after successful submit
      setAnswers({});
      setActiveKey(COUNSELOR_FOLLOWUP_SCHEMA[0].key);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  }, [selectedSubId, answers, errors]);

  const goPrev = () => !isFirst && setActiveKey(COUNSELOR_FOLLOWUP_SCHEMA[activeIndex - 1].key);
  const goNext = () => !isLast && setActiveKey(COUNSELOR_FOLLOWUP_SCHEMA[activeIndex + 1].key);

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto p-4 md:p-6" data-testid="counselor-followup-page">
        <div className="mb-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold text-[#1C1A19] tracking-tight">
                Counselor Follow-Up
              </h1>
              <p className="text-sm text-[#5C5955] mt-1 max-w-2xl">
                Submit structured counselor observations and follow-up notes into the
                subscriber&apos;s Biological Context input queue. Admin will review and promote.
              </p>
            </div>
            {selectedSubId ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setSelectedSubId(''); navigate('/admin/counselor-followup'); }}
                className="border-[#D6D1C8]"
                data-testid="counselor-change-sub-btn"
              >
                Change subscriber
              </Button>
            ) : null}
          </div>
        </div>

        {!selectedSubId ? (
          <SubscriberPicker
            subscribers={subscribers}
            query={query}
            onQuery={setQuery}
            onSelect={(id) => { setSelectedSubId(id); navigate(`/admin/counselor-followup/${id}`); }}
          />
        ) : (
          <div className="space-y-4">
            <IdentityStrip sub={selectedSub} intakeSummary={intakeSummary} viewerRole={user?.role} />
            <PerceptionContextSnippet subscriberId={selectedSubId} />

            <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4 md:gap-6">
              <aside className="md:sticky md:top-4 md:self-start">
                <Card className="bg-[#FBFAF7] border-[#EBE8E3]">
                  <CardContent className="p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-xs text-[#8A8681]">Form sections</span>
                      <Badge variant="outline" className="text-[10px] border-[#C89B7B] text-[#7A4A2B]">
                        {completeness}%
                      </Badge>
                    </div>
                    <SectionNav
                      schema={COUNSELOR_FOLLOWUP_SCHEMA}
                      activeKey={activeKey}
                      answers={answers}
                      onSelect={setActiveKey}
                      tidPrefix="cf"
                    />
                  </CardContent>
                </Card>
              </aside>

              <main>
                <Card className="bg-white border-[#EBE8E3]">
                  <CardContent className="p-5 md:p-7">
                    <div className="mb-5">
                      <div className="text-xs text-[#8A8681]">
                        Section {activeIndex + 1} of {COUNSELOR_FOLLOWUP_SCHEMA.length}
                      </div>
                      <h2 className="text-xl font-semibold text-[#1C1A19] mt-1" data-testid="counselor-active-section-title">
                        {activeSection.title}
                      </h2>
                    </div>

                    <SchemaSection
                      section={activeSection}
                      answers={answers}
                      onSectionChange={onSectionChange}
                      tidPrefix="cf"
                    />

                    <div className="mt-7 flex items-center justify-between gap-3">
                      <Button
                        variant="outline"
                        onClick={goPrev}
                        disabled={isFirst}
                        className="border-[#D6D1C8]"
                        data-testid="counselor-prev-btn"
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                      </Button>

                      <div className="flex items-center gap-2">
                        {isLast ? (
                          <Button
                            onClick={submit}
                            disabled={submitting}
                            className="bg-[#2C2A29] hover:bg-[#1C1A19] text-white"
                            data-testid="counselor-submit-btn"
                          >
                            {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                            Submit follow-up
                          </Button>
                        ) : (
                          <Button
                            onClick={goNext}
                            className="bg-[#2C2A29] hover:bg-[#1C1A19] text-white"
                            data-testid="counselor-next-btn"
                          >
                            Next <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </main>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
